import React, { useEffect, useState } from 'react'
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout'
import { ClientWizard } from '@/components/clients/ClientWizard'
import { IntakeSubmission, buildPrefillClient } from '@/components/clients/intake-prefill'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileText, Sparkles, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export default function ClientWizardPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  // Unlinked intake submissions offered as wizard prefill (create-client-
  // from-intake). Selecting one seeds the form; the wizard itself stays in
  // create mode. After creation the submission is marked linked + applied.
  const [submissions, setSubmissions] = useState<IntakeSubmission[]>([])
  const [selected, setSelected] = useState<IntakeSubmission | null>(null)

  useEffect(() => {
    (supabase as any)
      .from('intake_submissions')
      .select('*')
      .is('linked_client_id', null)
      .order('created_at', { ascending: false })
      .limit(25)
      .then(({ data }: { data: IntakeSubmission[] | null }) => setSubmissions(data || []))
  }, [])

  const handleComplete = async (client: any) => {
    if (selected && client?.id) {
      const { error } = await (supabase as any)
        .from('intake_submissions')
        .update({ linked_client_id: client.id, applied_at: new Date().toISOString() })
        .eq('id', selected.id)
      if (error) {
        console.error('Failed to mark intake submission applied:', error)
        toast({
          title: 'Note',
          description: 'Client created, but the intake submission could not be marked as applied. Link it from the client page.',
        })
      }
    }
    navigate(`/clients/${client.id}`)
  }

  const handleCancel = () => {
    navigate('/clients')
  }

  return (
    <NewAppLayout>
      <div className="space-y-4">
        {submissions.length > 0 && !selected && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> Start from an intake submission
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-muted-foreground">
                These intake form submissions aren't linked to a client yet. Picking one pre-fills the wizard; you can still edit everything before saving.
              </p>
              {submissions.map(s => (
                <div key={s.id} className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <div className="font-medium text-sm">
                      {s.company_name || s.contact_email || 'Unnamed submission'}
                      {s.research_state === 'done' && (
                        <Badge className="ml-2" variant="secondary">
                          <Sparkles className="h-3 w-3 mr-1" />ICP researched
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[s.website, s.contact_email, new Date(s.created_at).toLocaleDateString()].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setSelected(s)}>Use this</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {selected && (
          <div className="flex items-center justify-between border rounded-md p-3 bg-muted/50">
            <span className="text-sm">
              Pre-filled from intake submission of{' '}
              <span className="font-medium">{selected.company_name || selected.contact_email}</span>
              {' '}({new Date(selected.created_at).toLocaleDateString()}). It will be marked as applied when the client is created.
            </span>
            <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>
              <X className="h-4 w-4 mr-1" /> Start blank instead
            </Button>
          </div>
        )}

        {/* key remounts the wizard so the form re-seeds when the choice changes */}
        <ClientWizard
          key={selected?.id || 'blank'}
          intakePrefill={selected ? buildPrefillClient(selected) : undefined}
          onComplete={handleComplete}
          onCancel={handleCancel}
        />
      </div>
    </NewAppLayout>
  )
}
